import { useEffect } from 'react'

const ZOOM_TOLERANCE_PX = 1

/**
 * Chat owns the viewport: the timeline is the only scroll container.
 *
 * Two cooperating mechanisms:
 *
 * 1. `chat-mode` locks `html`/`body` overflow (and fixes the body) so the
 *    document itself cannot scroll.
 * 2. `.chat-page` is pinned to the *visual* viewport band: iOS pans and zooms
 *    the visual viewport when the keyboard opens (and after pinch gestures),
 *    which carries layout-anchored fixed elements above the visible area. The
 *    hook measures the full visible band — offset left/top, width and height,
 *    at any zoom scale — and publishes it as CSS variables that position
 *    `.chat-page` exactly over the visible area, the same way the Flutter web
 *    engine sizes its canvas to `visualViewport` (`width/height × scale`).
 *
 * iOS does not reliably fire `visualViewport` resize/scroll events during the
 * keyboard animation or pinch zoom, so — like the Flutter engine, which also
 * falls back to a `matchMedia('(resolution: …)')` listener — this hook does
 * not rely on events: it measures every frame and writes only when the band
 * changes.
 */
export function useChatViewportLock(): void {
  useEffect(() => {
    const root = document.documentElement
    const body = document.body
    root.classList.add('chat-mode')
    body.classList.add('chat-mode')
    window.scrollTo(0, 0)

    const viewport = window.visualViewport
    if (!viewport) {
      return () => {
        root.classList.remove('chat-mode')
        body.classList.remove('chat-mode')
      }
    }

    let frame = 0
    let last = ''

    const measure = () => {
      frame = window.requestAnimationFrame(measure)
      const zoomed =
        Math.abs(viewport.width - window.innerWidth) > ZOOM_TOLERANCE_PX ||
        Math.abs(viewport.height - window.innerHeight) > ZOOM_TOLERANCE_PX ||
        viewport.offsetTop > 0 ||
        viewport.offsetLeft > 0
      const next = zoomed
        ? `${Math.round(viewport.offsetLeft)},${Math.round(viewport.offsetTop)},${Math.round(viewport.width)},${Math.round(viewport.height)}`
        : ''
      if (next === last) return
      last = next
      if (zoomed) {
        root.style.setProperty('--chat-vv-offset-left', `${viewport.offsetLeft}px`)
        root.style.setProperty('--chat-vv-offset', `${viewport.offsetTop}px`)
        root.style.setProperty('--chat-vv-width', `${Math.round(viewport.width)}px`)
        root.style.setProperty('--chat-vv-height', `${Math.round(viewport.height)}px`)
        root.classList.add('chat-vv-anchored')
      } else {
        root.style.removeProperty('--chat-vv-offset-left')
        root.style.removeProperty('--chat-vv-offset')
        root.style.removeProperty('--chat-vv-width')
        root.style.removeProperty('--chat-vv-height')
        root.classList.remove('chat-vv-anchored')
      }
    }

    frame = window.requestAnimationFrame(measure)

    return () => {
      window.cancelAnimationFrame(frame)
      root.classList.remove('chat-mode')
      body.classList.remove('chat-mode')
      root.classList.remove('chat-vv-anchored')
      root.style.removeProperty('--chat-vv-offset-left')
      root.style.removeProperty('--chat-vv-offset')
      root.style.removeProperty('--chat-vv-width')
      root.style.removeProperty('--chat-vv-height')
    }
  }, [])
}
